import {
  Component,
  HostListener,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import * as Highcharts from 'highcharts';
import { takeUntil } from 'rxjs/operators';
import { SnapshotService } from '../../Services/snapshot.service';
import { CustomsnapshotService } from '../../Services/customsnapshot.service';
import * as _ from 'lodash';
import { LoaderService } from 'src/app/common/services/loader.service';

@Component({
  selector: 'app-custom-output-icons',
  templateUrl: './custom-output-icons.component.html',
  styleUrls: ['./custom-output-icons.component.css'],
})
export class CustomOutputIconsComponent implements OnChanges, OnDestroy {
  constructor(
    public _configService: SnapshotService,
    private _customService: CustomsnapshotService,
    private renderer: Renderer2,
    private _loaderService: LoaderService,
  ) {}
  @Input() data: any;
  @Input() widgetchartname: string = '';
  @Input() id: any;
  @Input() alldata: any;
  @Input() datafromLayout: any;
  @Input() multimetricselect: boolean = false;
  @Output() dataBarsUpdated = new EventEmitter<{
    item: any;
    status: boolean;
  }>();
  @Input() legendItems: any[] = [];
  trendlineOutputView: number = 0;
  legendDropdownOpen = false;

  hiddenSeriesMap: { [name: string]: boolean } = {};

  @HostListener('document:click')
  onLegendDocumentClick(): void {
    this.legendDropdownOpen = false;
  }

  @HostListener('window:kpi-legend-open', ['$event'])
  onOtherLegendOpen(e: any): void {
    if (e.detail?.id !== this.id) {
      this.legendDropdownOpen = false;
    }
  }

  toggleLegendDropdown(event: Event): void {
    event.stopPropagation();
    this.legendDropdownOpen = !this.legendDropdownOpen;
    if (this.legendDropdownOpen) {
      window.dispatchEvent(
        new CustomEvent('kpi-legend-open', { detail: { id: this.id } }),
      );
    }
  }

  toggleSeries(leg: any, event: Event): void {
    event.stopPropagation();
    if (leg.isKPI) return;
    const containerId = 'chart-container' + this.id;
    const chart = (Highcharts as any).charts.find(
      (c: any) => c && c.renderTo && c.renderTo.id === containerId,
    );
    if (!chart) return;
    const series = chart.series.find((s: any) => s.name === leg.name);
    if (!series) return;
    const newVisible = !series.visible;
    series.setVisible(newVisible, true);
    // Spread creates a new object → Angular detects the change
    this.hiddenSeriesMap = { ...this.hiddenSeriesMap, [leg.name]: !newVisible };
  }

  infoTextTitle: string = '';
  showExpandPopup = false;
  visualization: any;
  infoClicked: boolean = false;
  infoTextMessage: string = '';
  EnableDatabar: boolean = false;
  metricdropdownOutput: boolean = false;
  // id: any;
  item: any;
  elementList: any = false;
  customization: any = {};
  isdropDown: any = false;
  dropDownItems: any = [];
  inputPopup: boolean = false;
  brandList: any = [];
  selectedBrand: any;
  metricList: any[] = [];
  selectedMetric: any;
  originalWidgetChartName: string = '';
  isSingleFunnelOrMultipleWidget: boolean = false;
  isSingleFunnel: boolean = false;
  defaultSorting = 0;
  isSortAvailable: boolean = false;

  // --- Cached properties so the template never calls methods or reads service
  //     properties on every change-detection cycle ---
  isMultiSnapshot: boolean = false;
  isAudienceSnapshot: boolean = false;
  isMultiOrCohort: boolean = false; // multiSnapshot OR multiCohort
  isFunnelChart: boolean = false;
  isPropellerChart: boolean = false;
  isBrandIndependentWidget: boolean = false;
  additionalGroupLength: number = 0;
  cachedPopupWidgetName: string = '';
  cachedBrandDisplay: string = 'Select';
  uniqueBrandList: any[] = [];

  private destroy$ = new Subject<void>();

  private _refreshSnapshotTypeFlags() {
    const t = (this._configService.selectedSnapshotType ?? '').toLowerCase();
    this.isMultiSnapshot = t.includes('multi');
    this.isAudienceSnapshot = t.includes('audience');
    this.isMultiOrCohort =
      this.isMultiSnapshot || !!this._configService.multiCohort;
  }

  private resolveSourceChartType(widget: any): string {
    return (
      widget?.sourceWidgetChartName ??
      widget?.widgetchart_name ??
      ''
    )
      .toString()
      .trim()
      .toLowerCase();
  }

  private isGaugeWidget(): boolean {
    return this.resolveSourceChartType(this.data) === 'gauge';
  }

  private _refreshBrandIndependentFlag() {
    this.isBrandIndependentWidget =
      this.isGaugeWidget() ||
      this.data?.useMetricTrendSeries === true ||
      this.data?.isBrandDependent === false ||
      this.customization?.bdBiToggle === false ||
      this.customization?.bdBiToggle === 'false';
  }

  ngOnInit() {
    this._refreshSnapshotTypeFlags();
    this._customService
      .getBrandList(this.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => {
        let brandList = list[0];
        this.selectedBrand = list[1];
        this._customService.funnelChartSelectedItem$.next(
          this.selectedBrand || '',
        );
        this.brandList = [];
        if (brandList) {
          brandList.forEach((b: any) => {
            this.brandList.push({
              option: b,
              checked: b == this.selectedBrand ? true : false,
            });
          });
        }
        this.uniqueBrandList = this.getUniqueBrand(this.brandList);
        this.cachedBrandDisplay = this.getBrand();
      });
    let customizationProperties = JSON.parse(
      this.data?.customizationProperties,
    );
    this.trendlineOutputView = customizationProperties.trendlineOutputView;
    this.EnableDatabar = customizationProperties.isBarDataEnable;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['legendItems']) {
      this.hiddenSeriesMap = {};
    }
    if (changes['data']) {
      this.customization = JSON.parse(this.data?.customizationProperties);
      this.trendlineOutputView = this.customization.trendlineOutputView;
      this.metricdropdownOutput =
        this.data.metricdropdownOutput != 0 && !!this.data.metricdropdownOutput;
      this.additionalGroupLength = this.LengthOf(
        this.customization?.additionalGroup,
      );
      this._refreshSnapshotTypeFlags(); // snapshot type may update between data reloads
      this._refreshBrandIndependentFlag();
      this.cachedPopupWidgetName = this.getPopupWidgetName(this.data);
      if (this.data.elementList != undefined) {
        this.data.elementList.forEach((item: any) => {
          if (item.item == 'Sorting') {
            this.isSortAvailable = true;
          }
        });
      }

      if (this.data.sortingOrder != undefined) {
        this.data.sortingOrder = (this.data.sortingOrder + 1) % 3;
      }
      if (
        (this.customization?.additionalGroup &&
          this.data.elementList == undefined) ||
        this.data.elementList?.length == 0
      ) {
        this.elementListItems = []; // reset to prevent stale items from a previous widget being pushed again
        // if (this.customization?.showLegend !== undefined) {
        //   // Add the "Show Legend" property to the additionalGroup object

        //   this.customization.additionalGroup['showLegend'] = this.customization.showLegend;
        // }
        // let elementListItems = Object.keys(this.customization?.additionalGroup);
        let elementListItems = Object.keys(
          this.customization?.additionalGroup || {},
        ).filter(
          (key) =>
            this.customization.additionalGroup[key] === true ||
            (key === 'InfoText' && !!this.customization.additionalGroup[key]),
        );
        // elementListItems.push(Object.keys(this.customization?.additionalGroup || {}).filter(
        //   key => this.customization.additionalGroup[key] === true
        // ))
        if (elementListItems) {
          elementListItems.forEach((e: any) => {
            // if(e == "FreezeFirstColumn" || e=="FreezeFirstRow" || e=="HideNA" || e=="Transpose"){
            //   this.elementListItems.push(
            //     {
            //       item: e,
            //       selected: this.customization?.additionalGroup[e],
            //       isdropDown: false,
            //       options: []
            //     }
            //   );
            // }
            // else if( e== "ColumnHeaderSorting"){
            //   this.elementListItems.push(
            //     {
            //       item: e,
            //       selected: false,
            //       isdropDown: false,
            //       options: [
            //         { item: "Ascending", selected: false },
            //         { item: "Descending", selected: false },
            //         { item: "Default", selected: true },
            //       ]
            //     }
            //   );
            // }
            if (
              e != 'Sorting' &&
              e != 'ColumnHeaderSorting' &&
              e != 'TrendlineinExports' &&
              e != 'showLegend'
            ) {
              this.elementListItems.push({
                item: e,
                selected: e === 'HideNA' ? true : false,
                isdropDown: false,
                options: [],
              });
            } else if (
              e != 'ColumnHeaderSorting' &&
              e != 'TrendlineinExports' &&
              e != 'showLegend'
            ) {
              this.elementListItems.push({
                item: e,
                selected: false,
                isdropDown: false,
                options: [
                  { item: 'Ascending', selected: false },
                  { item: 'Descending', selected: false },
                  { item: 'Default', selected: true },
                ],
              });
            }
          });
        }
        // Sort this.elementListItems based on chartOptions
        this.elementListItems = this.elementListItems
          .map((item: any) => {
            // Find the matching object from chartOptions
            const matchedOption = this._customService.elementLists.find(
              (option) =>
                option.name.replace(/\s+/g, '').toLowerCase() ===
                item.item.replace(/\s+/g, '').toLowerCase(),
            );

            // If a match is found, add the label and sortId
            if (matchedOption) {
              return {
                ...item,
                label: matchedOption.name, // Add label from chartOptions
                sortId: matchedOption.sortId, // Add sortId from chartOptions
              };
            }

            // If no match, return the item with a high sortId to move it to the end
            return {
              ...item,
              label: item.item, // Use the item name as the label
              sortId: Number.MAX_SAFE_INTEGER,
            };
          })
          .sort((a: any, b: any) => a.sortId - b.sortId); // Sort based on sortId

        // const chartType = this.data?.chartType || this.widgetchartname;
        //   if ((chartType?.toLowerCase() === 'propeller' || this.widgetchartname?.toLowerCase() === 'propeller chart') &&!this._configService.selectedSnapshotType?.toLowerCase().includes('multi')) {
        //     this.data.trendlineOutputView = 1;
        //   } else {
        //     this.data.trendlineOutputView = this.customization?.trendlineOutputView;
        //   }
        this.data.elementList = this.elementListItems;
        this.data.sortingOrder = 0;
        this.data.elementList.forEach((item: any) => {
          if (item.item == 'Sorting') {
            this.isSortAvailable = true;
          }
        });
        if (
          this._configService.selectedSnapshotType
            .toLowerCase()
            .includes('multi')
        ) {
          if (
            !this.data.metric.some((dd: any) =>
              dd.metric_value.some((mv: any) =>
                mv.data.some((data: any) =>
                  data.hasOwnProperty('defaultIndex'),
                ),
              ),
            )
          ) {
            // If 'defaultIndex' doesn't exist in any data entry, add it with incremental values starting from 0
            this.data.metric = this.data.metric.map((dd: any) => ({
              ...dd,
              metric_value: dd.metric_value.map((mv: any) => ({
                ...mv,
                data: mv.data.map((data: any, index: number) => ({
                  ...data,
                  defaultIndex: index, // Assign incremental value starting from 0
                })),
              })),
            }));
          }
        }
      }
    }
    if (changes['data'] || changes['multimetricselect']) {
      if (this.multimetricselect) {
        this.metricdropdownOutput =
          this.data.metricdropdownOutput != 0 &&
          !!this.data.metricdropdownOutput;
        const savedMetric = this._customService.getSelectedMetricSync(
          this.id.toString(),
        );
        const isLollipopMetricMode = this.isLollipopMetricMode();
        let effectiveMetric = '';

        if (isLollipopMetricMode) {
          const brandOptions = this.getLollipopBrandOptions(this.data);
          const defaultOption = brandOptions[0] || '';
          const resolvedMetric =
            savedMetric && brandOptions.includes(savedMetric)
              ? savedMetric
              : defaultOption;

          effectiveMetric = resolvedMetric;
          this.metricList = brandOptions.map((brand: string) => ({
            option: brand,
            checked: brand === effectiveMetric,
          }));
        } else {
          const defaultOption = this.data.metric[0].metric_value[0]?.option || '';
          const resolvedMetric =
            savedMetric &&
            this.data.metric[0].metric_value.some(
              (mv: any) => mv.option === savedMetric,
            )
              ? savedMetric
              : defaultOption;

          const metrics = this.getEntryPointFilteredMetrics(
            this.data.metric[0].metric_value,
          );
          const uniqueMetrics = this._customService.uniqueMetricOptions || [];

          const validMetrics = this.getValidMetricValues(metrics, uniqueMetrics);

          effectiveMetric =
            validMetrics.some((mv: any) => mv.option === resolvedMetric)
              ? resolvedMetric
              : (validMetrics[0]?.option || '');

          this.metricList = validMetrics.map((mv: any) => ({
            option: mv.option,
            checked: mv.option === effectiveMetric,
          }));
        }

        this.selectedMetric = effectiveMetric;
        this.data.selectedMetric = effectiveMetric;
        if (this.metricdropdownOutput) {
          this.selectedMetric = effectiveMetric;
          this.data.selectedMetric =
            this.metricList.length >= 1 ? this.selectedMetric : '';
          this._customService.updateSelectedMetric(
            this.id,
            this.metricList.length >= 1 ? this.selectedMetric : '',
          );
          this.metricSelected.emit({
            widgetchartname: this.widgetchartname,
            selectedMetric:
              this.metricList.length >= 1 ? this.selectedMetric : '',
          });
        }
      } else {
        this.metricList = [];
        this.selectedMetric = undefined;
      }
      this.cachedPopupWidgetName = this.getPopupWidgetName(this.data);
    }
    if (changes['id']) {
      this.isSingleFunnelOrMultipleWidget =
        this.datafromLayout.find(
          (x: any) => x.page == 'Page ' + this._customService.pageCount,
        )?.selectedLayout == 1
          ? this.widgetchartname == 'FUNNEL CHART' ||
            this.widgetchartname == 'Trend as Widget'
            ? true
            : false
          : true;
      this.isSingleFunnel =
        this.datafromLayout[this.id - 1]?.selectedLayout == 1 &&
        this.widgetchartname == 'FUNNEL CHART'
          ? true
          : false;
    }
    if (changes['widgetchartname'] || changes['id']) {
      this.isFunnelChart =
        (this.widgetchartname ?? '').toLowerCase() === 'funnel chart';
      this.isPropellerChart =
        (this.widgetchartname ?? '').toLowerCase() === 'propeller chart';
    }
  }

  getBrand() {
    const part = (this.selectedBrand ?? '').split('|')[1];
    return part || this.selectedBrand;
  }

  toogleDropdown() {
    this.isBrandListdropdownOpen = !this.isBrandListdropdownOpen;
  }

  onCloseIconClick(data: any) {
    this.infoClicked = false;
    this.infoTextMessage = '';
  }

  elementListItems: any = [];
  LengthOf(a: any) {
    if (a) {
      if (Array.isArray(a)) {
        // If array contains Sorting entries (either string or objects with an `item` prop), exclude them
        const filtered = a.filter((x: any) => {
          if (typeof x === 'string') return x !== 'Sorting';
          if (x && typeof x === 'object' && x.item) return x.item !== 'Sorting';
          return true;
        });
        return filtered.length;
      } else if (typeof a === 'object') {
        const keys = Object.keys(a);
        if (keys.length > 0) {
          // Exclude 'Sorting' key and count only keys whose value is strictly true
          const filteredKeys = keys.filter((k) => k !== 'Sorting');

          // If after removing 'Sorting' there are no keys, return 0
          if (filteredKeys.length === 0) return 0;

          // Count keys that are strictly true. Preserve Trendline behavior by counting it as 1 when truthy.
          let countTrue = 0;
          filteredKeys.forEach((k) => {
            if (k === 'Trendline') {
              if (a.Trendline) countTrue += 1;
            } else if (k === 'InfoText') {
              if (a[k] && a[k] !== '') countTrue += 1;
            } else {
              if (a[k] === true) countTrue += 1;
            }
          });

          return countTrue;
        }
      }
      return 0;
    } else {
      return 0;
    }
  }
  isBrandListdropdownOpen: boolean = false;
  isMetricDropdownOpen: boolean = false;
  checkBoxesbrandListClicked(event: any, item: any) {
    this.brandList.forEach((b: any) => {
      if (b.option == item.option) {
        b.checked = event.target.checked;
        this.selectedBrand = b.option;
        this.cachedBrandDisplay = this.getBrand();
        this._customService.updateSelectedBrand(this.id, this.selectedBrand);
      } else {
        b.checked = false;
      }
    });
  }

  checkBoxesMetricListClicked(event: any, item: any) {
    if (this.shouldSyncMetricSelection()) {
      this.metricSelectionRequested.emit({
        metric: item.option,
        sourceId: this.id,
      });
      return;
    }
    this.applyMetricSelection(item.option);
  }

  shouldSyncMetricSelection(): boolean {
    return (
      this.multimetricselect &&
      this.metricdropdownOutput &&
      this.metricList.length > 1 &&
      !this.isBrandIndependentWidget &&
      this.isPlanParticipantsSnapshot()
    );
  }

  private isPlanParticipantsSnapshot(): boolean {
    const snapshotName =
      this._configService.outputSnapshotData?.snapshot_name ||
      this._configService.snapshotSelectedName ||
      '';

    return snapshotName.toString().toUpperCase().includes('PLAN PARTICIPANTS');
  }

  applyMetricSelection(metric: string): boolean {
    const selectedItem = this.metricList.find((item) => item.option === metric);
    if (!selectedItem) {
      return false;
    }

    this.metricList.forEach((item) => {
      item.checked = item.option === metric;
    });
    this.selectedMetric = metric;
    this.cachedPopupWidgetName = this.getPopupWidgetName(this.data);
    this.data.selectedMetric = metric;
    this._customService.updateSelectedMetric(this.id, metric);
    this.metricSelected.emit({
      widgetchartname: this.widgetchartname,
      selectedMetric: metric,
    });
    return true;
  }

  showExpandPopupFun() {
    //this.infoTextTitle = this.widgetName;

    if (this.data?.widgetchart_name === 'Trend as Widget') {
      // Open the same trend popup as when Trendline is clicked
      this.originalWidgetChartName = this.data.widgetchart_name;

      // Restore trendOutputDbCallData from this widget's own metric data,
      // in case it was overwritten by another widget's trendline click.
      const rawData: any[] = [];
      if (this.data.metric) {
        this.data.metric.forEach((metric: any) => {
          if (metric.metric_value) {
            metric.metric_value.forEach((mv: any) => {
              if (mv.data) {
                mv.data.forEach((d: any) => {
                  rawData.push({
                    ...d,
                    widgetName: d.widgetName || this.data.widget_name,
                    Level1ColChild:
                      d.Level1ColChild ?? d.lvl1Child ?? '',
                  });
                });
              }
            });
          }
        });
      }
      this._configService.trendOutputDbCallData = rawData;

      const m = _.cloneDeep(this.data);
      m.sourceWidgetChartName = this.data.widgetchart_name;
      m.widgetchart_name = 'Line Chart';

// const metricList = m.metric.flatMap((metric: any) =>
      //   metric.metric_value.map((mv: any) => mv.option != undefined ? mv.option : mv.metricName)
      // );
      const metricList = this.buildTrendPopupMetricList(m);


const brandList: any[] = Array.from(new Set(m.metric.flatMap((metric: any) =>
        metric.metric_value.flatMap((mv: any) => (mv.data || []).map((d: any) => d.colChild))
      )));

      this.trendData = m;
      this.metricLists = metricList;
      this.brandLists = this.isBrandIndependentWidget ? [] : brandList;
      this.data.widgetchart_name = 'Line Chart';
      this.showTrendPoppup = true;
    } else {
      this.showExpandPopup = true;
    }
  }
  receivedDeleteConfirmation(data: any) {
    this.showExpandPopup = data;
  }

  showelementlist() {
    if (this.LengthOf(this.customization?.additionalGroup) < 1) {
      return;
    }

    this.alldata.forEach((d: any) => {
      if (d != this.data) {
        d.showElementList = false;
      }
    });
    this.data.showElementList =
      this.data.showElementList == undefined
        ? true
        : !this.data.showElementList;
    this.elementList = !this.elementList;

    event?.stopPropagation();
  }
  closeelementList() {
    this.data.showElementList = false;
    this.elementList = false;
    this.isdropDown = false;
    this.data.elementList.forEach((item: any) => {
      if (item.item == 'Sorting') {
        item.selected = false;
        item.isdropDown = false;
      }
    });
  }
  closedropdown(item: any) {
    this.isdropDown = false;
    item.selected = false;
    item.isdropDown = false;
  }
  @Output() elementClickedStatus = new EventEmitter();
  @Output() metricSelected = new EventEmitter<{
    widgetchartname: string;
    selectedMetric: string;
  }>();
  @Output() metricSelectionRequested = new EventEmitter<{
    metric: string;
    sourceId: any;
  }>();
  currentElementList: any = [];
  showTrendPoppup: boolean = false;
  trendData: any;
  metricLists: any = [];
  brandLists: any = [];

  sortData() {
    this.defaultSorting = (this.data.sortingOrder + 1) % 3;
    let sortingOrder =
      this.defaultSorting == 1
        ? 'Ascending'
        : this.defaultSorting == 2
          ? 'Descending'
          : 'Default';
    let data = {
      data: this.data,
      value: sortingOrder.toLowerCase(),
      key: 'sort',
    };
    this.elementClickedStatus.emit(data);
  }
  onSortingClick(item: string) {
    this.data.elementList.forEach((e: any) => {
      if (e.item == item) {
        this.dropDownItems = e.options;
        this.sortData();
      }
    });
  }
  elementItemSelect(item: any, elementList: any, dropdownContainer: any) {
    this.data.elementList.forEach((e: any) => {
      if (e.item != 'Sorting' && e.item == item.item) {
        e.selected = !e.selected;
        if (e.item == 'InfoText') {
          this.infoTextMessage = this.customization.infoText;
          e.selected = false;
          this.infoClicked = true;
          this.infoTextTitle = this.data?.widget_name;
        }
        if (e.item == 'XAxisGridlines') {
          let data = {
            data: this.data,
            value: e.selected,
            key: 'xaxisgridlines',
          };
          setTimeout(() => {
            this.elementClickedStatus.emit(data);
          }, 0);
        }
        if (e.item == 'showLegend') {
          let data = {
            data: this.data,
            value: e.selected,
            key: 'showLegend',
          };
          setTimeout(() => {
            this.elementClickedStatus.emit(data);
          }, 0);
        }
        if (e.item == 'FreezeFirstColumn') {
          let data = {
            data: this.data,
            value: e.selected,
            key: 'freezeFirstColumn',
          };
          setTimeout(() => {
            this.elementClickedStatus.emit(data);
          }, 0);
        }
        if (e.item == 'FreezeFirstRow') {
          let data = {
            data: this.data,
            value: e.selected,
            key: 'freezeFirstRow',
          };
          setTimeout(() => {
            this.elementClickedStatus.emit(data);
          }, 0);
        }
        if (e.item == 'Transpose') {
          let data = {
            data: this.data,
            value: e.selected,
            key: 'Transpose1',
          };
          setTimeout(() => {
            this.elementClickedStatus.emit(data);
          }, 0);
        }
        if (e.item == 'DataLabelOverlap') {
          let data = {
            data: this.data,
            value: e.selected,
            key: 'dataLabelOverlap',
          };
          setTimeout(() => {
            this.elementClickedStatus.emit(data);
          }, 0);
        }
        if (e.item == 'Axes') {
          let data = {
            data: this.data,
            value: e.selected,
            key: 'axes',
          };
          setTimeout(() => {
            this.elementClickedStatus.emit(data);
          }, 0);
        }
        if (e.item == 'Sectorfill') {
          let data = {
            data: this.data,
            value: e.selected,
            key: 'sectorfill',
          };
          setTimeout(() => {
            this.elementClickedStatus.emit(data);
          }, 0);
        }
        // if (e.item == "TrendlineinExports") {
        //   let data = {
        //     data: this.data,
        //     value: e.selected,
        //     key: "TrendlineinExports"
        //   }
        //   setTimeout(() => {
        //     this.elementClickedStatus.emit(data);
        //   }, 0);
        // }
        if (e.item == 'HideNA') {
          let data = {
            data: this.data,
            value: e.selected,
            key: 'hideNA',
          };
          setTimeout(() => {
            this.elementClickedStatus.emit(data);
          }, 0);
        }
        if (e.item == 'YAxisGridlines') {
          let data = {
            data: this.data,
            value: e.selected,
            key: 'yaxisgridlines',
          };
          setTimeout(() => {
            this.elementClickedStatus.emit(data);
          }, 0);
        }
        if (e.item == 'Top10') {
          let data = {
            data: this.data,
            value: e.selected,
            key: 'top10',
          };
          setTimeout(() => {
            this.elementClickedStatus.emit(data);
          }, 0);
        }
        if (e.item == 'Trendline') {
          // Prefer sticky source type if a prior open left widgetchart_name as "Line Chart"
          this.originalWidgetChartName =
            this.data.sourceWidgetChartName || this.data.widgetchart_name;
          this._loaderService.showLoader();
          let widgetchart_name = this.originalWidgetChartName;
          let widgetData = this._configService.widgetData.filter(
            (d: any) => d.widget_name == this.data.widget_name,
          );
          this._configService
            .getOutPutData(
              this._configService.finalLeftPanelSubmitObject.previewData,
              // this._configService.finalLeftPanelSubmitObject.selectedClient,
              // this._configService.finalLeftPanelSubmitObject.selectedStudy,
              this._configService.getDropdownValues()?.clientName,
              this._configService.getDropdownValues()?.studyName,
              this._configService.finalLeftPanelSubmitObject.significance,
              this._configService.finalLeftPanelSubmitObject.isFilteringActive,
              this._configService.finalLeftPanelSubmitObject
                .DefaultConfidenceLevel,
              this._configService.finalLeftPanelSubmitObject.isTotalBase,
              this._configService.finalLeftPanelSubmitObject.selectedWeight,
              this._configService.finalLeftPanelSubmitObject.weightColumnName,
              this._configService.finalLeftPanelSubmitObject.isBoostFactor,
              this._configService.finalLeftPanelSubmitObject
                .SignificanceVsReference,
              this._configService.finalLeftPanelSubmitObject.columnLength,
              this._configService.finalLeftPanelSubmitObject.dataArray,
              this._configService.finalLeftPanelSubmitObject.isBrandAnalysis,
              this._configService.finalLeftPanelSubmitObject.isAggregatedData,
              this._configService.finalLeftPanelSubmitObject
                .isSemiAggregatedData,
              this._configService.finalLeftPanelSubmitObject.isRespondentLevel,
              this._configService.finalLeftPanelSubmitObject
                .aggregatedVariables,
              this._configService.finalLeftPanelSubmitObject.istimePeriodWave,
              this._configService.finalLeftPanelSubmitObject
                .widegtconfigdetails,
              this._configService.finalLeftPanelSubmitObject
                .indexVsBrandReference,
              this._configService.finalLeftPanelSubmitObject
                .significanceVsBrandReference,
              true, //this._configService.checkTrendEnabled(this.existingData)
              this._configService.widgetPageData,
              widgetData,
              this._configService.finalLeftPanelInputSelectedObject.page,
              false,
              this._configService.finalLeftPanelSubmitObject.customBaseMetric,
              this._configService.multiCohort,
              this._configService.finalLeftPanelSubmitObject?.productName ?? '',
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
              if (
                result.Data != null &&
                result.Data != undefined &&
                result.Data != ''
              ) {
                let opData = JSON.parse(result.Data);
                opData.forEach((d: any) => {
                  if (d.Level1ColChild?.startsWith('Time Period|')) {
                    d.Level1ColChild = d.Level1ColChild.replace(
                      'Time Period|',
                      '',
                    );
                  }
                });
                // if(this._configService.finalLeftPanelInputSelectedObject.compareAcross=="Multiple Brands"){
                //   opData = opData.filter((item: any) => item.rowChild === this.selectedMetric);
                // }
                // Find the reference brand's data for each metric
                let referenceDataMap: any = {};
                let count = 0;
                let baseselected =
                  this._configService.getDropdownValues()?.baseType;
                let referenceBrand =
                  this._configService.getDropdownValues()?.selectedIndex;
                opData.forEach((item: any) => {
                  if (
                    typeof item?.colChild === 'string' &&
                    item.colChild.includes(referenceBrand)
                  ) {
                    let key = '';
                    if (item.isCustomGroup || item.isCustomCalculation) {
                      key = `${item.rowChild}_${item.rowChild}_${item.widgetNumber}_${item.pageNumber}_${item.Level1ColChild}`;
                    } else {
                      key = `${item.rowChild}_${item.rowVariableCode}_${item.widgetNumber}_${item.pageNumber}_${item.Level1ColChild}`;
                    }
                    count++;
                    if (this._configService.weightSelected === 'Unweighted')
                      referenceDataMap[key] =
                        baseselected.toLowerCase() !== 'total base'
                          ? item.answerUnweightedShare
                          : item.totalUnweightedShare;
                    else
                      referenceDataMap[key] =
                        baseselected.toLowerCase() !== 'total base'
                          ? item.answerWeightedShare
                          : item.totalWeightedShare;
                  }
                });

                //adding NA to reference data for 0 or null value
                Object.keys(referenceDataMap).forEach((key) => {
                  if (
                    referenceDataMap[key] === null ||
                    referenceDataMap[key] === 0
                  ) {
                    referenceDataMap[key] = 'NA';
                  }
                });

                //try
                // Create a new array with the divided data
                let newArr = opData.map((item: any) => {
                  // Construct the key from rowVariableCode and widgetName
                  if (item.widgetName == 'Awarenesss') {
                    let x = 1;
                  }
                  let key = '';
                  if (item.isCustomGroup || item.isCustomCalculation) {
                    key = `${item.rowChild}_${item.rowChild}_${item.widgetNumber}_${item.pageNumber}_${item.Level1ColChild}`;
                  } else {
                    key = `${item.rowChild}_${item.rowVariableCode}_${item.widgetNumber}_${item.pageNumber}_${item.Level1ColChild}`;
                  }
                  const referenceValue = referenceDataMap[key];

                  if (referenceValue !== undefined) {
                    if (
                      this._configService.weightSelected === 'Weighted' ||
                      this._configService.weightSelected == 'Weight 1' ||
                      this._configService.weightSelected == 'weightB'
                    ) {
                      return {
                        ...item,
                        totalWeightedShareIndex:
                          referenceValue === 'NA'
                            ? null
                            : baseselected.toLowerCase() === 'total base'
                              ? (item.totalWeightedShare / referenceValue) * 100
                              : item.totalWeightedShare,
                        answerWeightedShareIndex:
                          referenceValue === 'NA'
                            ? null
                            : baseselected.toLowerCase() === 'answer base'
                              ? (item.answerWeightedShare / referenceValue) *
                                100
                              : item.answerWeightedShare,
                        ...(this._configService.isIndexSelected && {
                          CalculationTypeIndex: 'index',
                        }),
                      };
                    } else if (
                      this._configService.weightSelected === 'Unweighted'
                    ) {
                      return {
                        ...item,
                        totalWeightedShareIndex:
                          referenceValue === 'NA'
                            ? null
                            : baseselected.toLowerCase() === 'total base'
                              ? (item.totalUnweightedShare / referenceValue) *
                                100
                              : item.totalUnweightedShare,
                        answerWeightedShareIndex:
                          referenceValue === 'NA'
                            ? null
                            : baseselected.toLowerCase() === 'answer base'
                              ? (item.answerUnweightedShare / referenceValue) *
                                100
                              : item.answerUnweightedShare,
                        ...(this._configService.isIndexSelected && {
                          CalculationTypeIndex: 'index',
                        }),
                      };
                    }
                  }

                  return item;
                });
                opData = newArr;
                //end
                this._configService.trendOutputDbCallData = opData;

                let m = _.cloneDeep(
                  this._configService.itemWithTrendData[0].find(
                    (d: any) => d.widget_name == this.data.widget_name,
                  ),
                );

                if (!m?.metric?.length) {
                  this._loaderService.hideLoader();
                  return;
                }

                const isMultipleBrands =
                  this._configService.finalLeftPanelInputSelectedObject
                    .compareAcross == 'Multiple Brands';
                const sourceTypeEarly = (widgetchart_name ?? '')
                  .toString()
                  .trim()
                  .toLowerCase();
                const isGaugeTrend = sourceTypeEarly === 'gauge';

                const normTrendMetricKey = (raw: string | undefined): string => {
                  if (!raw) return '';
                  const text = raw.toString().trim();
                  if (!text.includes('|')) {
                    return text.replace(/\s+/g, '').toLowerCase();
                  }
                  const parts = text.split('|').map((p) => p.trim());
                  const last = parts[parts.length - 1] ?? '';
                  const resolved =
                    !last ||
                    last.toLowerCase() === 'yes' ||
                    last.toLowerCase() === 'no' ||
                    last.toLowerCase() === 't2b'
                      ? (parts[parts.length - 2] ?? text)
                      : last;
                  return resolved.replace(/\s+/g, '').toLowerCase();
                };

                const trendMetricMatchesRow = (
                  metricLabel: string | undefined,
                  row: any,
                ): boolean => {
                  const metricKey = normTrendMetricKey(metricLabel);
                  if (!metricKey) {
                    return false;
                  }
                  const candidates = [
                    row?.rowChild,
                    row?.colChild,
                    row?.Level1RowChild,
                    row?.Level1ColChild,
                  ];
                  return candidates.some((candidate) => {
                    const key = normTrendMetricKey(candidate);
                    if (!key) {
                      return false;
                    }
                    if (key === metricKey) {
                      return true;
                    }
                    const raw = (candidate ?? '').toString().toLowerCase();
                    const opt = (metricLabel ?? '').toString().trim().toLowerCase();
                    return (
                      raw.endsWith(opt) ||
                      raw.endsWith(`|${opt}`) ||
                      raw.endsWith(`| ${opt}`) ||
                      opt.startsWith(`${raw}:`) ||
                      raw.startsWith(`${opt}:`)
                    );
                  });
                };

                m.metric.forEach((metric: any) => {
                  metric.metric_value.forEach((mv: any) => {
                    let opDataMetric = opData.filter(
                      (d: any) =>
                        trendMetricMatchesRow(mv.option, d) ||
                        trendMetricMatchesRow(mv.metricName, d),
                    );

                    // Gauge: Sample Size / PPT use full trendOutputDbCallData; strict rowChild
                    // match was leaving mv.data empty and blanking Trend View only.
                    if (opDataMetric.length === 0 && isGaugeTrend) {
                      const widgetRows = opData.filter(
                        (d: any) =>
                          !d?.widgetName ||
                          d.widgetName === this.data.widget_name,
                      );
                      opDataMetric =
                        widgetRows.length > 0 ? widgetRows : opData;
                    }

                    opDataMetric = opDataMetric.map((d: any) => {
                      return {
                        ...d,
                        data: this._configService.weightToggle(
                          this._configService.getDropdownValues()?.baseType,
                          this._configService.weightSelected,
                          d,
                        ),
                        data2: this._configService.weightToggleIndex(
                          this._configService.getDropdownValues()?.baseType,
                          this._configService.weightSelected,
                          d,
                        ),
                        lvl1Child:
                          d.Level1ColChild ??
                          d.Level1RowChild ??
                          d.lvl1Child ??
                          '',
                        colChild: d.colChild,
                        rowChild: d.rowChild,
                      };
                    });

                    if (opDataMetric) {
                      mv.data = opDataMetric;
                      this._configService.sortTrendTimeporiodData(mv.data);
                    }
                  });
                });

                if (m) {
                  const sourceType = sourceTypeEarly;
                  m.sourceWidgetChartName = widgetchart_name;
                  m.widgetchart_name = 'Line Chart';
                  // Gauge clone only: force metric-over-time series (do not mutate dashboard widget flags)
                  if (isGaugeTrend) {
                    m.isBrandDependent = false;
                    m.useMetricTrendSeries = true;
                  }
                  let metricList: any = this.buildTrendPopupMetricList(m);
                  let brandList = Array.from(
                    new Set(
                      m.metric.flatMap((metric: any) =>
                        metric.metric_value.flatMap((m: any) =>
                          (m.data || []).map((d: any) => d.colChild),
                        ),
                      ),
                    ),
                  );
                  this.trendData = m;

                  this.metricLists = metricList;

                  this.brandLists =
                    isGaugeTrend || this.isBrandIndependentWidget
                      ? []
                      : brandList;
                  this.showTrendPoppup = true;
                }
                this.data.sourceWidgetChartName = widgetchart_name;
                this.data.widgetchart_name = 'Line Chart';
              }
              this._loaderService.hideLoader();
            });

          e.selected = false;
        }
      } else if (e.item == 'Sorting' && e.item == item.item) {
        e.selected = !e.selected;
        e.isdropDown = !e.isdropDown;
        this.isdropDown = e.isdropDown;
        this.dropDownItems = e.options;
        if (e.isdropDown) {
          this.toggleDropdown1(e.isdropDown, elementList, dropdownContainer);
        }
      }
      // else {
      //   if (e.isdropDown) {
      //     // e.selected = false;
      //     e.isdropDown = false;
      //     this.isdropDown = false;
      //   }
      // }
    });
    this._customService.elementListItems = _.cloneDeep(this.elementListItems);
    event?.stopPropagation();
  }
  showDropdown: boolean = false;
  @ViewChild('dropdownContainer') dropdownContainer!: ElementRef;
  private mergeAdditionalKpiIntoMetrics(
    metricsAvailable: any[],
    widget: any,
  ): any[] {
    const extra = widget?.additionalKPI;
    if (!Array.isArray(extra) || extra.length === 0) {
      return metricsAvailable;
    }
    const kpiBlock = extra.find((k: any) => {
      const opt = k?.kpi_option;
      if (opt == null) {
        return false;
      }
      // Align with chart-output bindCustomization / save payload
      return String(opt).replace(' : ', '') === 'Additional KPI';
    });

    if (!kpiBlock?.kpi_value?.length) {
      return metricsAvailable;
    }

    let kpiList: any[] = [];
    try {
      const raw = kpiBlock.kpi_value[0];
      kpiList =
        typeof raw === 'string'
          ? JSON.parse(raw || '[]')
          : Array.isArray(raw)
            ? raw
            : [];
    } catch {
      return metricsAvailable;
    }

    if (!Array.isArray(kpiList) || kpiList.length === 0) {
      return metricsAvailable;
    }

    const merged = [...metricsAvailable];
    let maxOrder = merged.reduce(
      (m, x) => Math.max(m, Number(x?.selectOrder) || 0),
      0,
    );
    for (const kpi of kpiList) {
      const opt = kpi?.option;
      if (opt == null || opt === '') {
        continue;
      }
      const existingIndex = merged.findIndex((m) => m.option === opt);
      if (existingIndex !== -1) {
        // Already in the standard metrics list — mark it as the KPI overlay
        merged[existingIndex] = {
          ...merged[existingIndex],
          isAdditionalKPI: true,
        };
        continue;
      }
      merged.push({
        ...kpi,
        selectOrder: ++maxOrder,
        data: Array.isArray(kpi.data) ? kpi.data : [],
        isAdditionalKPI: true, // ← ADD THIS
      });
    }
    return merged;
  }
  toggleDropdown1(
    isDropdown: any,
    elementList: any,
    dropdownContainer: any,
  ): void {
    this.showDropdown = !this.showDropdown;
    if (isDropdown) {
      // Calculate position of the clicked parent element
      const rect = elementList?.getBoundingClientRect();

      setTimeout(() => {
        const dropdown = this.dropdownContainer.nativeElement;
        // Calculate available space
        const viewportWidth = window.innerWidth;
        const dropdownWidth = dropdown.offsetWidth;

        let leftPosition: number;

        if (rect.right + dropdownWidth <= viewportWidth) {
          // Enough space on the right
          leftPosition = rect.right;
        } else {
          // Not enough space, position to the left
          leftPosition = rect.left - dropdownWidth;
        }

        // Set dropdown position
        this.renderer.setStyle(dropdown, 'position', 'fixed');
        this.renderer.setStyle(dropdown, 'top', `${rect.top}px`);
        this.renderer.setStyle(dropdown, 'left', `${leftPosition - 10}px`);
        this.renderer.setStyle(dropdown, 'z-index', '10');
        this.renderer.setStyle(dropdown, 'visibility', 'visible'); // Make sure dropdown is visible
      });
    }
  }

  getUniqueBrand(brandList: any[]): any[] {
    const seen = new Set<string>();
    return brandList.filter((item) => {
      if (seen.has(item.option)) {
        return false;
      }
      seen.add(item.option);
      return true;
    });
  }

  private checkMetricsPattern(metrics: string[]): number {
    let hasDelimiter = metrics.some((metric) => metric.includes('|'));
    if (!hasDelimiter) return 0;

    let firstParts = metrics.map((metric) => metric.split('|')[0]);
    let uniqueFirstParts = Array.from(new Set(firstParts));

    if (uniqueFirstParts.length === 1) {
      let allHaveFourParts = metrics.every(
        (metric) => metric.split('|').length === 4,
      );

      if (allHaveFourParts) {
        let firstThreeParts = metrics.map((metric) => {
          const parts = metric.split('|');
          return `${parts[0]}|${parts[1]}|${parts[2]}`;
        });
        let uniqueFirstThreeParts = Array.from(new Set(firstThreeParts));

        let lastParts = metrics.map((metric) => {
          const parts = metric.split('|');
          return parts[3];
        });
        let uniqueLastParts = Array.from(new Set(lastParts));

        // First 3 parts same, last part differs -> show last 2 parts
        if (uniqueFirstThreeParts.length === 1 && uniqueLastParts.length > 1) {
          return 5;
        }

        const firstTwoParts = metrics.map((metric) => {
          const parts = metric.split('|');
          return `${parts[0]}|${parts[1]}`;
        });
        const uniqueFirstTwoParts = Array.from(new Set(firstTwoParts));

        const thirdParts = metrics.map((metric) => {
          const parts = metric.split('|');
          return parts[2];
        });
        const uniqueThirdParts = Array.from(new Set(thirdParts));

        // First 2 parts same, 3rd part differs -> show 3rd and 4th parts
        if (uniqueFirstTwoParts.length === 1 && uniqueThirdParts.length > 1) {
          return 6;
        }

     
        

      
      }

      let allHaveThreeParts = metrics.every(
        (metric) => metric.split('|').length >= 3,
      );

      if (allHaveThreeParts) {
        let firstTwoParts = metrics.map((metric) => {
          const parts = metric.split('|');
          return `${parts[0]}|${parts[1]}`;
        });

        let uniqueFirstTwoParts = Array.from(new Set(firstTwoParts));

        if (uniqueFirstTwoParts.length > 1) {
          return 4;
        }
      }

      return 1;
    } else if (
      uniqueFirstParts.every((part) => part[0] === uniqueFirstParts[0][0])
    ) {
      return 2;
    } else {
      return 3;
    }
  }

  private formatMetricNameByPattern(metricName: any, type: number = 0): any {
    if (typeof metricName !== 'string') {
      return metricName;
    }

    if (type == 0) return metricName;
    else if (type == 1) {
      const parts = metricName.split('|');
      return parts[1];
    } else if (type == 4) {
      const parts = metricName.split('|');
      return parts[1];
    } else if (type == 5) {
      const parts = metricName.split('|');
      return parts[2] + '|' + parts[3]; // First 3 same, last differs -> last 2 parts
    } else if (type == 6) {
      const parts = metricName.split('|');
      return parts[2] + '|' + parts[3]; // First 2 same, 3rd differs -> 3rd and 4th parts
    }

    const parts = metricName.split('|');

    if (parts.length <= 2) {
      return metricName;
    }

    return parts.slice(-2).join('|').trim();
  }

  /** Empty for hide/filter — Answer Base null→NA, Total Base unused→0 (same as table). */
  private isEmptyMetricDataValue(value: any): boolean {
    return (
      value === undefined ||
      value === null ||
      value === 'NA' ||
      value === '' ||
      value == 0 ||
      value === '0'
    );
  }

  /** Distinct metric labels — same empty filter as table HideNA / Sample Size. */
  private buildTrendPopupMetricList(widget: any): string[] {
    const labelHasData = new Map<string, boolean>();
    const labelOrder: string[] = [];

    (widget?.metric || []).forEach((metricGroup: any) => {
      (metricGroup?.metric_value || []).forEach((mv: any) => {
        (mv?.data || []).forEach((row: any) => {
          const raw = (row?.rowChild ?? '').toString().trim();
          if (!raw) {
            return;
          }
          const label = raw.includes('|') ? raw.split('|')[1].trim() : raw;
          if (!label) {
            return;
          }
          const key = label.toLowerCase();
          if (!labelHasData.has(key)) {
            labelHasData.set(key, false);
            labelOrder.push(label);
          }
          if (!this.isEmptyMetricDataValue(row?.data)) {
            labelHasData.set(key, true);
          }
        });
      });
    });

    const fromRowChild = labelOrder.filter((label) =>
      labelHasData.get(label.toLowerCase()),
    );

    if (fromRowChild.length > 1) {
      return fromRowChild;
    }

    const uniqueMetrics = this._customService.uniqueMetricOptions || [];
    const validMetrics = this.getValidMetricValues(
      this.getEntryPointFilteredMetrics(widget?.metric?.[0]?.metric_value || []),
      uniqueMetrics,
    );
    const fromOptions = validMetrics
      .map((mv: any) => mv.option ?? mv.metricName)
      .filter((name: any) => !!name);

    return fromOptions.length > 0 ? fromOptions : fromRowChild;
  }

  private getEntryPointFilteredMetrics(metrics: any[] = []): any[] {
    let parsedMetrics: any[] = [];
    if (Array.isArray(metrics)) {
      parsedMetrics = metrics;
      if (metrics.length === 1 && typeof metrics[0] === 'string') {
        try {
          parsedMetrics = JSON.parse(metrics[0] || '[]');
        } catch {
          parsedMetrics = [];
        }
      }
    }

    if (!Array.isArray(parsedMetrics) || parsedMetrics.length === 0) {
      return [];
    }

    const metricOptions = parsedMetrics
      .map((metric: any) =>
        (metric?.option ?? metric?.metricName ?? '').toString(),
      )
      .filter((name: string) => !!name);

    const metricPatternType = this.checkMetricsPattern(metricOptions);

    return parsedMetrics.map((metric: any) => {
      const formattedOption = this.formatMetricNameByPattern(
        metric?.option,
        metricPatternType,
      );
      const formattedMetricName = this.formatMetricNameByPattern(
        metric?.metricName,
        metricPatternType,
      );

      return {
        ...metric,
        option: formattedOption,
        metricName: formattedMetricName,
      };
    });
  }

  private isBrandDependentMode(properties: any): boolean {
    const raw =
      properties?.branddependent ??
      properties?.BrandDependent ??
      properties?.bdBiToggle;

    if (raw === undefined || raw === null || raw === '') {
      return true;
    }

    if (typeof raw === 'string') {
      const normalized = raw.trim().toLowerCase();
      return normalized === 'true' || normalized === 'enable';
    }

    return !!raw;
  }

  get effectiveTrendLineView(): number {
    return this.isBrandIndependentWidget ? 1 : this.trendlineOutputView;
  }

  private isLollipopMetricMode(): boolean {
    const widgetName = (
      this.data?.widgetchart_name ||
      this.widgetchartname ||
      ''
    )
      .toString()
      .toLowerCase();
    const isLollipop = widgetName.includes('lollipop');
    if (!isLollipop) {
      return false;
    }

    let properties: any = {};
    try {
      properties = JSON.parse(this.data?.customizationProperties || '{}');
    } catch {
      properties = {};
    }

    if (!this.isBrandDependentMode(properties)) {
      return false;
    }

    return (
      (properties?.BrandMetricToggle || 'Brand').toString().trim().toLowerCase() ===
      'metric'
    );
  }

  private getLollipopBrandOptions(widget: any): string[] {
    const metricValues = widget?.metric?.[0]?.metric_value || [];
    const options = new Set<string>();

    metricValues.forEach((mv: any) => {
      (mv?.data || []).forEach((row: any) => {
        const rawValue = (row?.colChild || row?.rowChild || '').toString().trim();
        if (!rawValue) {
          return;
        }
        const parsedValue = rawValue.includes('|')
          ? rawValue.split('|').pop()?.trim() || ''
          : rawValue;
        if (parsedValue) {
          options.add(parsedValue);
        }
      });
    });

    return Array.from(options);
  }

  private getValidMetricValues(metrics: any[], uniqueMetrics: string[]): any[] {
    if (!Array.isArray(metrics) || metrics.length === 0) {
      return metrics;
    }

    const hasValidData = (metric: any): boolean =>
      Array.isArray(metric?.data) &&
      metric.data.some((d: any) => !this.isEmptyMetricDataValue(d?.data));

    const metricsWithData = metrics.filter((metric: any) => hasValidData(metric));

    if (uniqueMetrics.length === 0) {
      return metricsWithData.length > 0 ? metricsWithData : metrics;
    }

    const normalizedUniqueMetrics = uniqueMetrics
      .map((metric: string) => metric?.trim().toLowerCase())
      .filter(Boolean);

    const filteredMetrics = metricsWithData.filter((mv: any) => {
      const option = (mv?.option ?? mv?.metricName ?? '').trim().toLowerCase();
      return !!option && normalizedUniqueMetrics.includes(option);
    });

    if (filteredMetrics.length > 0) {
      return filteredMetrics;
    }

    return metricsWithData.length > 0 ? metricsWithData : metrics;
  }

  checkElementStatus() {
    if (
      this.elementList &&
      this.LengthOf(this.customization?.additionalGroup) > 0
    ) {
      if (this.LengthOf(this.customization?.additionalGroup) == 1) {
        if (
          Object.keys(this.customization?.additionalGroup).includes('InfoText')
        ) {
          if (this.customization?.additionalGroup?.InfoText != '') {
            return true;
          } else {
            return false;
          }
        } else {
          return true;
        }
      }
    } else {
      return false;
    }
    return true;
  }

  dropDownSelect(item: any) {
    this.dropDownItems.forEach((d: any) => {
      if (d.item == item.item) {
        d.selected = true;
        let data = {
          data: this.data,
          value: d.item.toLowerCase(),
          key: 'sort',
        };
        this.elementClickedStatus.emit(data);
        this.data.elementList.forEach((item: any) => {
          if (item.item == 'Sorting') {
            item.selected = false;
            item.isdropDown = false;
          }
        });
      } else {
        d.selected = false;
      }
    });
    event?.stopPropagation();
  }

  hide(data: any) {
    this.showTrendPoppup = data;
    this.data.widgetchart_name = this.originalWidgetChartName;
    delete this.data.sourceWidgetChartName;
    delete this.data.useMetricTrendSeries;
  }

  updateDataBars(item: any, status: boolean) {
    this.dataBarsUpdated.emit({ item, status });
  }

  getPopupWidgetName(item: any): string {
    if (!item?.widget_name) {
      return '';
    }

    let widgetName = item.widget_name;
    if (
      this.metricdropdownOutput &&
      this.metricList?.length > 1 &&
      this.selectedMetric &&
      !this.isBrandIndependentWidget
    ) {
      widgetName = `${widgetName} - ${this.selectedMetric}`;
    }

    const isAudienceSnapshot = this._configService.selectedSnapshotType
      ?.toLowerCase()
      ?.includes('audience');

    if (!isAudienceSnapshot) {
      return widgetName;
    }

    if (item?.audience != null) {
      return widgetName;
    }

    const brandLabel = this.getSelectedBrandLabel(item);

    if (!brandLabel) {
      return widgetName;
    }

    if (widgetName.includes(brandLabel)) {
      return widgetName;
    }

    return `${widgetName} <span class="itemBrandLabel">${brandLabel}</span>`;
  }

  getSelectedBrandLabel(item: any): string {
    // If audience is NOT null, return empty string
    if (item?.audience != null) {
      return '';
    }

    const value =
      this._configService?.finalLeftPanelSubmitObject?.previewData
        ?.Audiencebrand?.[0]?.option;

    if (!value) {
      return '';
    }
    const rawBrandName = value.split('|').pop() ?? value;
    const brandName = `(${rawBrandName})`;
    return brandName;
  }
}
